<template>
  <fieldset
    ref="containerRef"
    :class="$style.restageContainer"
    aria-label="Restage image group"
    data-test="restage-container"
  >
    <button
      :class="[$style.triggerButton, { [$style.triggerButtonIs24]: isPlatformIs24() }]"
      aria-haspopup="true"
      aria-label="Restage image trigger"
      data-test="restage-trigger"
      @click.stop="toggle"
      @keydown.escape="close"
    >
      <span :class="$style.triggerIcon" aria-hidden="true">✦</span>
    </button>

    <div
      v-if="isOpen"
      :class="$style.restageOptions"
      role="menu"
      aria-label="Restage image menu"
      data-test="restage-options"
    >
      <template v-for="option in options" :key="option.value">
        <HgButton
          v-if="option.value !== currentTransformation || isRestageError"
          :class="$style.restageOption"
          role="menuitem"
          :data-test="`restage-option-${option.value}`"
          @click="handleRestageOptionClick(option.value)"
        >
          {{ option.text }}
        </HgButton>

        <HgButton
          v-else
          :class="$style.restageOption"
          role="menuitem"
          :data-test="`restage-option-undo`"
          theme="secondary"
          @click="handleRestageOptionClick(null)"
        >
          {{ t('restage.option.undo') }}
        </HgButton>
      </template>
    </div>
  </fieldset>
</template>

<script lang="ts" setup>
import { HgButton } from '@smg-real-estate/domus';
import { ImageAnnotation } from '@smg-real-estate/hg-rets-schema';
import { get, has } from 'lodash-es';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { InteractionEvent } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import type { RestageTransformation } from '@/helpers/Restage/client';
import { DescribeImageType } from '@/helpers/Restage/image-utils';
import { useRestageStore } from '@/store/modules/restage';
import type { HgImage } from '@/store/types';

const RESTAGE_ACTION_MAP: Record<RestageTransformation, string> = {
  REMOVE_FURNITURE: 'remove_furniture',
  CHANGE_STYLE: 'change_look',
};

const RESTAGE_UNDO_ACTION_MAP: Record<RestageTransformation, string> = {
  REMOVE_FURNITURE: 'undo_remove_furniture',
  CHANGE_STYLE: 'undo_change_look',
};

const props = defineProps<{
  currentImage: HgImage;
  currentImageAnnotation: ImageAnnotation | undefined;
}>();

const { t } = useI18n();

const restageStore = useRestageStore();
const isOpen = ref<boolean>(false);

const options = computed(() => {
  const defaultOptions = [
    { text: t('restage.option.removeFurniture'), value: 'REMOVE_FURNITURE' },
    { text: t('restage.option.changeStyle'), value: 'CHANGE_STYLE' },
  ];
  const imageClassification = props.currentImageAnnotation?.classification;
  const isAnImageOfUnfurnishedRoom = imageClassification === DescribeImageType.UNFURNISHED_ROOM;

  return isAnImageOfUnfurnishedRoom ? defaultOptions.slice(1) : defaultOptions;
});

const containerRef = ref<HTMLElement | null>(null);
const currentTransformation = computed<RestageTransformation | null>(() => {
  const imageUrl = props.currentImage.src;
  return get(restageStore.transformationsByImageUrl, [imageUrl]) ?? null;
});
const isRestageError = computed(() => has(restageStore.errorImageUrls, [props.currentImage.src]));

const trackRestageEvent = (eventAction: string): void => {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_feature_group_001: 'ai_restage',
    h_event_action_007: eventAction,
    h_event_label_008: 'ai_restage',
  });
};

const toggle = (): void => {
  if (!isOpen.value && isRestageError.value) {
    restageStore.resetImageState(props.currentImage.src);
  }

  if (!isOpen.value) {
    trackRestageEvent('open_restage');
  }

  isOpen.value = !isOpen.value;
};

const close = (): void => {
  isOpen.value = false;
};

const handleClickOutside = (event: MouseEvent): void => {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    close();
  }
};

const handleRestageOptionClick = (value: string | null): void => {
  if (value === null) {
    if (currentTransformation.value) {
      trackRestageEvent(RESTAGE_UNDO_ACTION_MAP[currentTransformation.value]);
    }
    restageStore.undoImageTransformation(props.currentImage.src);
  } else {
    const transformation = value as RestageTransformation;
    trackRestageEvent(RESTAGE_ACTION_MAP[transformation]);
    restageStore.restageImage(props.currentImage.src, transformation);
  }
  close();
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

@keyframes spin-gradient {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.restageContainer {
  position: absolute;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: $space-75;
  z-index: 1;
  border: none;
  margin: 0;
  padding: 0;

  // Mobile: bottom center
  bottom: $space-100;
  left: 50%;
  transform: translateX(-50%);

  // Desktop: top right
  @media (min-width: $viewport-lg) {
    flex-direction: row-reverse;
    align-items: flex-start;
    top: $space-100;
    right: $space-100;
    bottom: auto;
    left: auto;
    transform: none;
  }
}

.triggerButton {
  position: relative;
  width: 36px;
  height: 36px;
  background: $color-white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition:
    transform $transition-duration $transition-easing,
    box-shadow $transition-duration $transition-easing;
  box-shadow: $elevation-low;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: -50%;
    background: conic-gradient(
      $color-orange-50,
      $color-magenta-50,
      $color-violet-50,
      $color-blue-50,
      $color-orange-50
    );
    animation: spin-gradient 3s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    background: $color-white;
    border-radius: 50%;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: $elevation-medium;
  }

  &:active {
    transform: scale(0.95);
  }
}

// IS24 gradient overrides HG default
.triggerButtonIs24::before {
  background: conic-gradient(
    $color-teal-110,
    $color-golden-100,
    $color-ff-blue-100,
    $color-peach-70,
    $color-teal-110
  );
}

.triggerIcon {
  position: relative;
  font-size: 16px;
  color: $color-black;
  // Icon: 2 > Base: 1
  z-index: 2;
}

.restageOptions {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  min-width: 200px;
}

.restageOption {
  box-shadow: $elevation-low;
  @include body-3-bold;

  &:hover {
    box-shadow: $elevation-medium;
  }
}
</style>
